import React from "react";
import { Layout } from "src/components/custom/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "src/components/ui/Card";
import { Badge } from "src/components/ui/Badge";
import { Shield, Lock, Eye, Trash2, Globe, Phone } from "lucide-react";

export default function PrivacyPolicy() {
  return (
    <Layout>
      <Layout.Body>
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
          {/* Header */}
          <div className="relative overflow-hidden bg-gradient-to-br from-green-50 via-emerald-50 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-700 border-b border-slate-200/50 dark:border-slate-700/50">
            <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 to-emerald-600/5 dark:from-green-400/5 dark:to-emerald-300/5" />
            <div className="relative px-6 py-12 md:px-8 md:py-16">
              <div className="mx-auto max-w-4xl text-center">
                <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-green-500 to-emerald-600 shadow-lg">
                  <Shield className="h-8 w-8 text-white" />
                </div>
                <h1 className="mb-4 text-4xl font-bold tracking-tight text-slate-900 dark:text-white md:text-5xl">
                  Privacy Policy
                </h1>
                <p className="mx-auto max-w-2xl text-lg text-slate-600 dark:text-slate-300 mb-6">
                  This Privacy Policy describes Our policies and procedures on the collection, use and disclosure of Your information when You use the Service.
                </p>
                <Badge variant="outline" className="text-slate-600 border-slate-300 bg-slate-50/50 dark:text-slate-400 dark:border-slate-600 dark:bg-slate-800/30">
                  Last updated: August 21, 2024
                </Badge>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-12 md:px-8">
            <div className="mx-auto max-w-4xl space-y-8">

              {/* Introduction */}
              <Card className="border-slate-200/60 bg-white/95 dark:bg-slate-900/95 dark:border-slate-700/60 backdrop-blur-sm">
                <CardContent className="p-6 text-slate-700 dark:text-slate-300">
                  <p className="mb-4">
                    We use Your Personal data to provide and improve the Service. By using the Service, You agree to the collection and use of information in accordance with this Privacy Policy. This Privacy Policy has been created with the help of the{" "}
                    <a
                      href="https://www.privacypolicies.com/privacy-policy-generator/"
                      target="_blank"
                      rel="noreferrer"
                      className="text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300"
                    >
                      Privacy Policy Generator
                    </a>.
                  </p>
                </CardContent>
              </Card>

              {/* Interpretation and Definitions */}
              <Card className="border-slate-200/60 bg-white/95 dark:bg-slate-900/95 dark:border-slate-700/60 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3 text-2xl text-slate-900 dark:text-white">
                    <Eye className="h-6 w-6 text-green-600 dark:text-green-400" />
                    Interpretation and Definitions
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6 text-slate-700 dark:text-slate-300">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-3">Interpretation</h3>
                    <p>
                      The words of which the initial letter is capitalized have meanings defined under the following conditions. 
                      The following definitions shall have the same meaning regardless of whether they appear in singular or in plural.
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Definitions</h3>
                    <p className="mb-4">For the purposes of this Privacy Policy:</p>
                    
                    <div className="grid gap-3 md:grid-cols-2">
                      <div className="rounded-lg bg-slate-50/80 dark:bg-slate-800/50 p-4">
                        <strong className="text-slate-900 dark:text-white">Account</strong> means a unique account created for You to access our Service or parts of our Service.
                      </div>
                      <div className="rounded-lg bg-slate-50/80 dark:bg-slate-800/50 p-4">
                        <strong className="text-slate-900 dark:text-white">Affiliate</strong> means an entity that controls, is controlled by or is under common control with a party.
                      </div>
                      <div className="rounded-lg bg-slate-50/80 dark:bg-slate-800/50 p-4">
                        <strong className="text-slate-900 dark:text-white">Application</strong> refers to Dharam kaata, the software program provided by the Company.
                      </div>
                      <div className="rounded-lg bg-slate-50/80 dark:bg-slate-800/50 p-4">
                        <strong className="text-slate-900 dark:text-white">Company</strong> refers to Dharam Kaata.
                      </div>
                      <div className="rounded-lg bg-slate-50/80 dark:bg-slate-800/50 p-4">
                        <strong className="text-slate-900 dark:text-white">Cookies</strong> are small files placed on Your computer, mobile device containing browsing history details.
                      </div>
                      <div className="rounded-lg bg-slate-50/80 dark:bg-slate-800/50 p-4">
                        <strong className="text-slate-900 dark:text-white">Country</strong> refers to: Jharkhand, India
                      </div>
                      <div className="rounded-lg bg-slate-50/80 dark:bg-slate-800/50 p-4">
                        <strong className="text-slate-900 dark:text-white">Device</strong> means any device that can access the Service such as a computer, cellphone or digital tablet.
                      </div>
                      <div className="rounded-lg bg-slate-50/80 dark:bg-slate-800/50 p-4">
                        <strong className="text-slate-900 dark:text-white">Personal Data</strong> is any information that relates to an identified or identifiable individual.
                      </div>
                      <div className="rounded-lg bg-slate-50/80 dark:bg-slate-800/50 p-4">
                        <strong className="text-slate-900 dark:text-white">Service</strong> refers to the Application or the Website or both.
                      </div>
                      <div className="rounded-lg bg-slate-50/80 dark:bg-slate-800/50 p-4">
                        <strong className="text-slate-900 dark:text-white">Service Provider</strong> means any natural or legal person who processes the data on behalf of the Company.
                      </div>
                      <div className="rounded-lg bg-slate-50/80 dark:bg-slate-800/50 p-4">
                        <strong className="text-slate-900 dark:text-white">Usage Data</strong> refers to data collected automatically, either generated by the use of the Service.
                      </div>
                      <div className="rounded-lg bg-slate-50/80 dark:bg-slate-800/50 p-4">
                        <strong className="text-slate-900 dark:text-white">Website</strong> refers to Dharam Kaata, accessible from{" "}
                        <a
                          href="https://www.dharamkaata.in"
                          rel="external nofollow noopener noreferrer"
                          target="_blank"
                          className="text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300"
                        >
                          https://www.dharamkaata.in
                        </a>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Data Collection */}
              <Card className="border-slate-200/60 bg-white/95 dark:bg-slate-900/95 dark:border-slate-700/60 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-2xl text-slate-900 dark:text-white">Collecting and Using Your Personal Data</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6 text-slate-700 dark:text-slate-300">
                  
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Types of Data Collected</h3>
                    
                    <div className="space-y-4">
                      <div className="rounded-lg border border-green-200/60 bg-green-50/30 dark:bg-green-950/20 dark:border-green-800/30 p-4">
                        <h4 className="font-semibold text-green-900 dark:text-green-100 mb-2">Personal Data</h4>
                        <p className="text-sm text-green-800 dark:text-green-200 mb-3">
                          While using Our Service, We may ask You to provide Us with certain personally identifiable information that can be used to contact or identify You:
                        </p>
                        <ul className="space-y-2 text-sm text-green-800 dark:text-green-200">
                          <li className="flex items-center gap-2">
                            <div className="h-1.5 w-1.5 rounded-full bg-green-500" />
                            First name and last name
                          </li>
                          <li className="flex items-center gap-2">
                            <div className="h-1.5 w-1.5 rounded-full bg-green-500" />
                            Phone number
                          </li>
                          <li className="flex items-center gap-2">
                            <div className="h-1.5 w-1.5 rounded-full bg-green-500" />
                            Address, State, Province, ZIP/Postal code, City
                          </li>
                          <li className="flex items-center gap-2">
                            <div className="h-1.5 w-1.5 rounded-full bg-green-500" />
                            Usage Data
                          </li>
                        </ul>
                      </div>

                      <div className="rounded-lg border border-blue-200/60 bg-blue-50/30 dark:bg-blue-950/20 dark:border-blue-800/30 p-4">
                        <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">Usage Data</h4>
                        <p className="text-sm text-blue-800 dark:text-blue-200 mb-2">Usage Data is collected automatically when using the Service.</p>
                        <p className="text-sm text-blue-800 dark:text-blue-200">
                          Usage Data may include information such as Your Device's Internet Protocol address, browser type, browser version, pages visited, time and date of visit, time spent on pages, unique device identifiers and other diagnostic data.
                        </p>
                      </div>

                      <div className="rounded-lg border border-purple-200/60 bg-purple-50/30 dark:bg-purple-950/20 dark:border-purple-800/30 p-4">
                        <h4 className="font-semibold text-purple-900 dark:text-purple-100 mb-2">Information Collected while Using the Application</h4>
                        <p className="text-sm text-purple-800 dark:text-purple-200 mb-2">
                          While using Our Application, in order to provide features of Our Application, We may collect, with Your prior permission:
                        </p>
                        <p className="text-sm text-purple-800 dark:text-purple-200">
                          Pictures and other information from your Device's camera and photo library
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Tracking Technologies and Cookies */}
              <Card className="border-amber-200/60 bg-amber-50/30 dark:bg-amber-950/20 dark:border-amber-800/30">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3 text-2xl text-amber-900 dark:text-amber-100">
                    <Lock className="h-6 w-6 text-amber-600 dark:text-amber-400" />
                    Tracking Technologies and Cookies
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-amber-800 dark:text-amber-200 text-sm">
                  <p>We use Cookies and similar tracking technologies to track the activity on Our Service and store certain information.</p>
                  
                  <div className="space-y-4">
                    <div className="rounded-lg bg-amber-100/50 dark:bg-amber-900/30 p-4">
                      <h4 className="font-semibold mb-2">Necessary / Essential Cookies</h4>
                      <p className="mb-1">Type: Session Cookies | Administered by: Us</p>
                      <p>These Cookies are essential to provide You with services available through the Website and enable You to use some of its features.</p>
                    </div>
                    <div className="rounded-lg bg-amber-100/50 dark:bg-amber-900/30 p-4">
                      <h4 className="font-semibold mb-2">Cookies Policy / Notice Acceptance Cookies</h4>
                      <p className="mb-1">Type: Persistent Cookies | Administered by: Us</p>
                      <p>These Cookies identify if users have accepted the use of cookies on the Website.</p>
                    </div>
                    <div className="rounded-lg bg-amber-100/50 dark:bg-amber-900/30 p-4">
                      <h4 className="font-semibold mb-2">Functionality Cookies</h4>
                      <p className="mb-1">Type: Persistent Cookies | Administered by: Us</p>
                      <p>These Cookies allow us to remember choices You make when You use the Website, such as login details or language preference.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Use of Personal Data */}
              <Card className="border-slate-200/60 bg-white/95 dark:bg-slate-900/95 dark:border-slate-700/60 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-2xl text-slate-900 dark:text-white">Use of Your Personal Data</CardTitle>
                </CardHeader>
                <CardContent className="text-slate-700 dark:text-slate-300 text-sm">
                  <p className="mb-4">The Company may use Personal Data for the following purposes:</p>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <div className="mt-2 h-2 w-2 rounded-full bg-blue-500" />
                      <span><strong>To provide and maintain our Service</strong>, including to monitor the usage of our Service.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="mt-2 h-2 w-2 rounded-full bg-blue-500" />
                      <span><strong>To manage Your Account:</strong> to manage Your registration as a user of the Service.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="mt-2 h-2 w-2 rounded-full bg-blue-500" />
                      <span><strong>For the performance of a contract:</strong> the development, compliance and undertaking of the purchase contract.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="mt-2 h-2 w-2 rounded-full bg-blue-500" />
                      <span><strong>To contact You:</strong> To contact You by email, telephone calls, SMS, or other equivalent forms of electronic communication.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="mt-2 h-2 w-2 rounded-full bg-blue-500" />
                      <span><strong>To provide You</strong> with news, special offers and general information about other goods and services.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="mt-2 h-2 w-2 rounded-full bg-blue-500" />
                      <span><strong>For business transfers:</strong> We may use Your information to evaluate or conduct a merger, divestiture, restructuring or reorganization.</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              {/* Key Policies Grid */}
              <div className="grid gap-8 md:grid-cols-2">
                
                <Card className="border-slate-200/60 bg-white/95 dark:bg-slate-900/95 dark:border-slate-700/60 backdrop-blur-sm">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3 text-xl text-slate-900 dark:text-white">
                      <Trash2 className="h-5 w-5 text-red-600 dark:text-red-400" />
                      Delete Your Personal Data
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 text-slate-700 dark:text-slate-300 text-sm">
                    <p>You have the right to delete or request that We assist in deleting the Personal Data that We have collected about You.</p>
                    <p>Our Service may give You the ability to delete certain information about You from within the Service.</p>
                    <p>You may update, amend, or delete Your information at any time by signing in to Your Account and visiting the account settings section.</p>
                  </CardContent>
                </Card>

                <Card className="border-slate-200/60 bg-white/95 dark:bg-slate-900/95 dark:border-slate-700/60 backdrop-blur-sm">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3 text-xl text-slate-900 dark:text-white">
                      <Lock className="h-5 w-5 text-green-600 dark:text-green-400" />
                      Security of Your Personal Data
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-slate-700 dark:text-slate-300 text-sm">
                    <p>The security of Your Personal Data is important to Us, but remember that no method of transmission over the Internet, or method of electronic storage is 100% secure. While We strive to use commercially acceptable means to protect Your Personal Data, We cannot guarantee its absolute security.</p>
                  </CardContent>
                </Card>

                <Card className="border-slate-200/60 bg-white/95 dark:bg-slate-900/95 dark:border-slate-700/60 backdrop-blur-sm">
                  <CardHeader>
                    <CardTitle className="text-xl text-slate-900 dark:text-white">Children's Privacy</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 text-slate-700 dark:text-slate-300 text-sm">
                    <p>Our Service does not address anyone under the age of 13. We do not knowingly collect personally identifiable information from anyone under the age of 13.</p>
                    <p>If We need to rely on consent as a legal basis for processing Your information and Your country requires consent from a parent, We may require Your parent's consent before We collect and use that information.</p>
                  </CardContent>
                </Card>

                <Card className="border-slate-200/60 bg-white/95 dark:bg-slate-900/95 dark:border-slate-700/60 backdrop-blur-sm">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3 text-xl text-slate-900 dark:text-white">
                      <Globe className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      Links to Other Websites
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 text-slate-700 dark:text-slate-300 text-sm">
                    <p>Our Service may contain links to other websites that are not operated by Us. If You click on a third party link, You will be directed to that third party's site.</p>
                    <p>We have no control over and assume no responsibility for the content, privacy policies or practices of any third party sites or services.</p>
                  </CardContent>
                </Card>
              </div>

              {/* Changes to Privacy Policy */}
              <Card className="border-slate-200/60 bg-white/95 dark:bg-slate-900/95 dark:border-slate-700/60 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-2xl text-slate-900 dark:text-white">Changes to this Privacy Policy</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-slate-700 dark:text-slate-300 text-sm">
                  <p>We may update Our Privacy Policy from time to time. We will notify You of any changes by posting the new Privacy Policy on this page.</p>
                  <p>We will let You know via email and/or a prominent notice on Our Service, prior to the change becoming effective and update the "Last updated" date at the top of this Privacy Policy.</p>
                  <p>You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.</p>
                </CardContent>
              </Card>

              {/* Contact Us */}
              <Card className="border-blue-200/60 bg-blue-50/30 dark:bg-blue-950/20 dark:border-blue-800/30">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3 text-2xl text-blue-900 dark:text-blue-100">
                    <Phone className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                    Contact Us
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-blue-800 dark:text-blue-200">
                  <p className="mb-4">If you have any questions about this Privacy Policy, You can contact us:</p>
                  <div className="rounded-lg bg-blue-100/50 dark:bg-blue-900/30 p-4">
                    <p className="font-medium">By email: yuvraj@dharamkaata.com</p>
                  </div>
                </CardContent>
              </Card>

            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-slate-200/50 dark:border-slate-700/50 bg-slate-50/50 dark:bg-slate-900/50 px-6 py-8 md:px-8">
            <div className="mx-auto max-w-4xl text-center">
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Generated using{" "}
                <a
                  href="https://www.privacypolicies.com/privacy-policy-generator/"
                  target="_blank"
                  rel="noreferrer"
                  className="text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300"
                >
                  Privacy Policies Generator
                </a>
              </p>
            </div>
          </div>
        </div>
      </Layout.Body>
    </Layout>
  );
}